import { max, min } from 'lodash';
import { ExpeditionComputeRepartitionMode } from 'enums/expedition';
import { ExpeditionClientPortion, ExpeditionConfirmedClientPortion } from 'types';
import { ExpeditionFormLine, RepartitionByIndexInForm } from 'types/expedition';
import { postExpeditionComputeRepartition } from './apiOperations';
import { calculatePercentage, calculateWeightFromPercentage } from './math';

export async function computeRepartitionWeightFromPercentage(
  tonnageExpedie: number,
  repartitionEnPourcentage: string[],
  numberOfDecimals = 3,
) {
  return await postExpeditionComputeRepartition({
    mode: ExpeditionComputeRepartitionMode.MODE_WEIGHT_FROM_PERCENTAGE,
    tonnageExpedie,
    repartitionEnPourcentage,
    repartitionEnTonnage: [],
    numberOfDecimals,
  });
}

export async function computeRepartitionPercentageFromWeight(
  tonnageExpedie: number,
  repartitionEnTonnage: string[] | { [key: string]: string },
  numberOfDecimals = 4,
) {
  return await postExpeditionComputeRepartition({
    mode: ExpeditionComputeRepartitionMode.MODE_PERCENTAGE_FROM_WEIGHT,
    tonnageExpedie,
    repartitionEnPourcentage: [],
    repartitionEnTonnage,
    numberOfDecimals,
  });
}

export function getTonnageReprisInTons(
  expeditionClientPortion: ExpeditionClientPortion,
  expeditionConfirmedClientPortion: ExpeditionConfirmedClientPortion | null | undefined,
): number {
  if (expeditionClientPortion.isUpdatedRepartitionAccepteeRepreneur) {
    return (expeditionClientPortion.expeditionRepartitionAccepteeRepreneur?.tonnageRepris ?? 0) / 1000;
  } else if (expeditionConfirmedClientPortion) {
    return expeditionConfirmedClientPortion.weight / 1000;
  } else {
    return expeditionClientPortion.tonnageReprisInTons ?? 0;
  }
}

export function computeRepartitionByContracts(
  contratsClos: ExpeditionFormLine[],
  contratsEnCours: ExpeditionFormLine[],
  tonnageExpedieKilos: number,
): RepartitionByIndexInForm {
  const totalStockDispoContratsClos = contratsClos.reduce(
    (previousValue, currentValue) => previousValue + currentValue.tonnageEnStockDispoKilos,
    0,
  );
  const totalStockDispoContratsEnCours = contratsEnCours.reduce(
    (previousValue, currentValue) => previousValue + currentValue.tonnageEnStockDispoKilos,
    0,
  );

  const totalTonnageToBeDistributedContratsClos = min([totalStockDispoContratsClos, tonnageExpedieKilos]) ?? 0;
  const totalTonnageToBeDistributedContratsEnCours = max([0, tonnageExpedieKilos - totalStockDispoContratsClos]) ?? 0;

  const repartitionContratsClos: RepartitionByIndexInForm = {};
  const repartitionContratsEnCours: RepartitionByIndexInForm = {};

  contratsClos.forEach(
    expeditionFormLine =>
      (repartitionContratsClos[expeditionFormLine.index] = Math.round(
        calculateWeightFromPercentage(
          calculatePercentage(expeditionFormLine.tonnageEnStockDispoKilos, totalStockDispoContratsClos),
          totalTonnageToBeDistributedContratsClos,
        ),
      )),
  );

  if (0 < totalTonnageToBeDistributedContratsEnCours) {
    contratsEnCours.forEach(
      expeditionFormLine =>
        (repartitionContratsEnCours[expeditionFormLine.index] = Math.round(
          calculateWeightFromPercentage(
            calculatePercentage(expeditionFormLine.tonnageEnStockDispoKilos, totalStockDispoContratsEnCours),
            totalTonnageToBeDistributedContratsEnCours,
          ),
        )),
    );
  }

  // 1. Calculez le tonnage total distribué.
  const totalDistributedTonnage =
    Object.values(repartitionContratsClos).reduce((acc, val) => acc + val, 0) +
    Object.values(repartitionContratsEnCours).reduce((acc, val) => acc + val, 0);

  // 2. Comparez-le au tonnage total attendu (`tonnageExpedieKilos`).
  const delta = tonnageExpedieKilos - totalDistributedTonnage;

  // Vérifiez si l'écart absolu est supérieur à 2 kilos.
  if (totalDistributedTonnage > 0) {
    if (tonnageExpedieKilos < totalStockDispoContratsClos) {
      // Si le tonnage expédié est inférieur ou égal au stock disponible des contrats clos
      const maxTonnageContract = contratsClos.reduce((maxContract, currentContract) => {
        return currentContract.tonnageEnStockDispoKilos > maxContract.tonnageEnStockDispoKilos
          ? currentContract
          : maxContract;
      });
      repartitionContratsClos[maxTonnageContract.index] += delta;
    } else if (contratsEnCours.length > 0) {
      // Si des contrats "en cours" existent, trouvez celui avec le tonnage le plus élevé.
      const maxTonnageContract = contratsEnCours.reduce((maxContract, currentContract) => {
        return currentContract.tonnageEnStockDispoKilos > maxContract.tonnageEnStockDispoKilos
          ? currentContract
          : maxContract;
      });
      if (delta !== 0) {
        repartitionContratsEnCours[maxTonnageContract.index] += delta;
      }
    } else {
      if (tonnageExpedieKilos <= totalStockDispoContratsClos) {
        // Sinon, trouvez le contrat "clos" avec le tonnage disponible le plus élevé.
        // ET LE TONNAGE EXPEDITIER ET INFFERIEUR AU TONAGE DISPONIBLE SINON ON FAIT PAS LE DETLA.
        const maxTonnageContract = contratsClos.reduce((maxContract, currentContract) => {
          return currentContract.tonnageEnStockDispoKilos > maxContract.tonnageEnStockDispoKilos
            ? currentContract
            : maxContract;
        });
        repartitionContratsClos[maxTonnageContract.index] += delta;
      }
    }
  }

  return { ...repartitionContratsClos, ...repartitionContratsEnCours };
}

export function transformRepartitionByIndexInFormObjectToArray(repartition: RepartitionByIndexInForm): string[] {
  const repartitionTonnage: string[] = [];
  for (const [key, value] of Object.entries(repartition)) {
    repartitionTonnage[parseInt(key)] = value;
  }
  return repartitionTonnage;
}
